import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from 'lib/utils';
import { AiMonogram } from './AiMonogram';
// Normalize AI math output: convert \[...\] → $$...$$ and \(...\) → $...$
function normalizeMath(text: string): string {
    return text
        .replace(/\\\[([\s\S]*?)\\\]/g, (_m, inner) => `$$\n${inner}\n$$`)
        .replace(/\\\(([\s\S]*?)\\\)/g, (_m, inner) => `$${inner}$`);
}
// Quiz answers come back as "**A)** text" paragraphs. They read as lettered options, not as prose.
const OPTION_LETTER = /^[A-Ha-h][).]$/;
export const Strong: React.FC<{ children?: React.ReactNode }> = ({ children }) => (<strong className="font-semibold text-foreground">{children}</strong>);
const optionLetter = (node: React.ReactNode): string | null => {
    if (!React.isValidElement<{ children?: React.ReactNode }>(node) || node.type !== Strong) return null;
    const text = node.props.children;
    return typeof text === 'string' && OPTION_LETTER.test(text.trim()) ? text.trim() : null;
};
export const Paragraph: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
    const [first, ...rest] = React.Children.toArray(children);
    const letter = optionLetter(first);
    // Several options folded into one paragraph stay prose: styling only the first would look broken.
    if (letter && !rest.some((node) => optionLetter(node))) {
        return (<p className="mb-1.5 flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2 leading-snug last:mb-0">
            <span className="mt-px flex h-5 min-w-5 shrink-0 items-center justify-center rounded-md bg-card px-1 text-[11.5px] font-bold text-foreground shadow-xs">
              {letter.replace(/[).]$/, '')}
            </span>
            <span className="min-w-0 flex-1">{rest}</span>
          </p>);
    }
    return <p className="mb-2.5 last:mb-0">{children}</p>;
};
interface MessageProps {
    text: string;
    type: 'user' | 'ai';
    id?: string;
    is_voice?: boolean;
    className?: string;
}
const Message: React.FC<MessageProps> = ({ text, type, id, is_voice, className }) => {
    const { t } = useTranslation();
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    if (type === 'ai') {
        // Grey "AI" monogram, then a column: the "AI o'qituvchi" label (with the
        // copy action at its end, shown on hover/focus) over the answer.
        return (<div className="group flex w-full max-w-[92%] gap-2.5">
        <AiMonogram />

        <div className="flex min-w-0 flex-1 flex-col gap-2.5">
          <div className="flex min-h-5 items-center justify-between gap-2">
            <span className="text-[12.5px] font-semibold text-muted-foreground">{t('ai_tutor')}</span>
            <button type="button" onClick={handleCopy} className="inline-flex shrink-0 items-center gap-1 rounded text-xs text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [.group:focus-within_&]:opacity-100 [.group:hover_&]:opacity-100">
              {copied ? (<><Check className="h-3 w-3"/><span>{t('copied')}</span></>) : (<><Copy className="h-3 w-3"/><span>{t('copy')}</span></>)}
            </button>
          </div>

          <div className={cn('min-w-0 overflow-x-hidden text-sm leading-[1.65] text-foreground [overflow-wrap:anywhere]', className)}>
            <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]} components={{
                h1: ({ children }) => (<h1 className="mb-2 mt-3 text-base font-bold leading-snug text-foreground first:mt-0">
                    {children}
                  </h1>),
                h2: ({ children }) => (<h2 className="mb-1.5 mt-3 text-sm font-semibold text-foreground first:mt-0">
                    {children}
                  </h2>),
                h3: ({ children }) => (<h3 className="mb-1 mt-2 text-sm font-medium text-foreground first:mt-0">
                    {children}
                  </h3>),
                p: Paragraph,
                ul: ({ children }) => (<ul className="mb-2.5 list-disc space-y-0.5 pl-4">{children}</ul>),
                ol: ({ children }) => (<ol className="mb-2.5 list-decimal space-y-0.5 pl-4">{children}</ol>),
                li: ({ children }) => <li className="mb-0.5 leading-snug">{children}</li>,
                blockquote: ({ children }) => (<blockquote className="my-2 border-l-2 border-border pl-3 italic text-muted-foreground">
                    {children}
                  </blockquote>),
                strong: Strong,
                em: ({ children }) => (<em className="italic">{children}</em>),
                table: ({ children }) => (<div className="my-3 overflow-x-auto">
                    <table className="w-full border-collapse overflow-hidden rounded-lg border border-border text-xs">
                      {children}
                    </table>
                  </div>),
                thead: ({ children }) => (<thead className="bg-muted">{children}</thead>),
                th: ({ children }) => (<th className="border border-border px-3 py-2 text-left font-semibold text-foreground">
                    {children}
                  </th>),
                td: ({ children }) => (<td className="border border-border px-3 py-2 text-foreground/80">
                    {children}
                  </td>),
                hr: () => <hr className="my-4 border-border/60"/>,
                code: ({ className: codeClass, children, ...props }) => {
                    const isBlock = !!codeClass;
                    return isBlock ? (<pre className="my-2 overflow-x-auto rounded-lg bg-gray-900 p-3 font-mono text-xs text-gray-100 dark:bg-gray-950">
                      <code className={codeClass} {...props}>{children}</code>
                    </pre>) : (<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs text-foreground" {...props}>
                      {children}
                    </code>);
                },
            }}>
              {normalizeMath(text)}
            </ReactMarkdown>
          </div>
        </div>
      </div>);
    }
    // User message: an ink bubble, its bottom-right corner tucked (12/12/4/12).
    return (<div className="flex w-full justify-end">
      <div className={cn('min-w-0 max-w-[78%] overflow-x-hidden rounded-xl rounded-br-sm bg-foreground px-3.5 py-2.5 text-sm leading-normal text-background [overflow-wrap:anywhere]', is_voice ? 'italic' : '', className)}>
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
            p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
        }}>
          {is_voice ? `"${text}"` : text}
        </ReactMarkdown>
      </div>
    </div>);
};
export const AdvancedMessage: React.FC<{
    children: React.ReactNode;
    className?: string;
    type?: 'ai' | 'user';
    id?: string;
}> = ({ children, className, type }) => {
    const content = (<div className="w-fit rounded-lg relative text-left leading-relaxed text-gray-900 dark:text-gray-100 group p-0 pt-1 bg-background">
      {children}
    </div>);
    return (<div className={cn('w-full flex', type === 'ai' ? 'justify-start' : 'justify-end', className)}>
      {content}
    </div>);
};
export default Message;
